import deepmerge from 'deepmerge';
import { get, set } from 'js-cookie';

import {
	CHAT_HISTORY_AUDIT_ACCESS_KEY_COOKIE_NAME,
	REFRESH_FLAG_COOKIE_NAME,
	SESSION_COOKIE_NAME,
	TAB_INSTANCES_COOKIE_NAME,
	isReadOnlyMode,
	rootDomain,
	sessionIdOverride,
} from '../globals';
import { State } from '../store';
import { oneDay } from './date';

export type SessionCookie = Pick<
	State,
	| 'sessionId'
	| 'liveChatAutoDisconnectNoticeReceivedTimestamp'
	| 'liveChatAutoDisconnectNoticeDismissedTimestamp'
	| 'lastInteractionTimestamp'
	| 'toastNotificationsLog'
>;

export interface TabInstance {
	id: string;
	registeredAt: number;
}

export interface TabInstancesCookie {
	activeTabId: string;
	tabInstances: TabInstance[];
}

export interface ChatHistoryAuditAccessKey {
	value: string;
	expiryTimestamp: number;
}

export interface ChatHistoryAuditAccessKeyCookie {
	dev?: ChatHistoryAuditAccessKey;
	qa?: ChatHistoryAuditAccessKey;
	live?: ChatHistoryAuditAccessKey;
}

export function removeIllegalKeysFromState(
	state: Partial<State>
): SessionCookie {
	const {
		sessionId,
		liveChatAutoDisconnectNoticeReceivedTimestamp,
		liveChatAutoDisconnectNoticeDismissedTimestamp,
		lastInteractionTimestamp,
		toastNotificationsLog,
		...illegalKeys // eslint-disable-line @typescript-eslint/no-unused-vars
	} = state;

	const cleanState = {} as SessionCookie;

	if (sessionId !== undefined) {
		cleanState.sessionId = sessionId;
	}
	if (state.hasOwnProperty('liveChatAutoDisconnectNoticeReceivedTimestamp')) {
		cleanState.liveChatAutoDisconnectNoticeReceivedTimestamp =
			liveChatAutoDisconnectNoticeReceivedTimestamp;
	}
	if (state.hasOwnProperty('liveChatAutoDisconnectNoticeDismissedTimestamp')) {
		cleanState.liveChatAutoDisconnectNoticeDismissedTimestamp =
			liveChatAutoDisconnectNoticeDismissedTimestamp;
	}
	if (state.hasOwnProperty('lastInteractionTimestamp')) {
		cleanState.lastInteractionTimestamp = lastInteractionTimestamp;
	}
	if (state.hasOwnProperty('toastNotificationsLog')) {
		cleanState.toastNotificationsLog = toastNotificationsLog;
	}
	/* eslint-enable no-undefined */

	return cleanState;
}

export function getPersistentState(): SessionCookie {
	if (isReadOnlyMode && sessionIdOverride) {
		return {
			sessionId: sessionIdOverride,
		};
	}

	const storedData = get(SESSION_COOKIE_NAME);
	return storedData
		? (removeIllegalKeysFromState(JSON.parse(storedData)) as SessionCookie)
		: ({} as SessionCookie);
}

export function setPersistentState(state: Partial<State>): void {
	if (isReadOnlyMode) {
		return;
	}

	const newState = deepmerge(
		getPersistentState(),
		removeIllegalKeysFromState(state)
	);

	if (rootDomain === 'localhost') {
		// The cookie is undefined if a domain option is set on localhost
		set(SESSION_COOKIE_NAME, JSON.stringify(newState));
	} else {
		set(SESSION_COOKIE_NAME, JSON.stringify(newState), {
			domain: rootDomain,
		});
	}
}

export function getSessionId(): undefined | string {
	const sessionData = getPersistentState();
	return sessionData && sessionData.sessionId
		? sessionData.sessionId
		: undefined;
}

export function getLastInteractionTimestamp(): undefined | number {
	const sessionData = getPersistentState();
	return sessionData && sessionData.lastInteractionTimestamp
		? sessionData.lastInteractionTimestamp
		: undefined;
}

export function shouldRetrieveRemoteSession(): boolean {
	if (isReadOnlyMode) {
		return true;
	}
	// If chatbot was opened more than 1 day ago, we don't want
	// to retrieve the remote session because it will be stale.
	const sessionId = getSessionId();
	const lastInteractionTimestamp = getLastInteractionTimestamp();
	if (
		sessionId &&
		lastInteractionTimestamp &&
		Date.now() - lastInteractionTimestamp < oneDay
	) {
		return true;
	}
	return false;
}

export function getRefreshStateFlagCookie(): boolean {
	const data = get(REFRESH_FLAG_COOKIE_NAME);
	return data ? JSON.parse(data) : false;
}

export function setRefreshStateFlagCookie(shouldRefresh: boolean): void {
	if (rootDomain === 'localhost') {
		// The cookie is undefined if a domain option is set on localhost
		set(REFRESH_FLAG_COOKIE_NAME, shouldRefresh.toString());
	} else {
		set(REFRESH_FLAG_COOKIE_NAME, shouldRefresh.toString(), {
			domain: rootDomain,
		});
	}
}

function removeTabInstancesOlderThan(milliseconds: number, timeNow: number) {
	return (instance: TabInstance) =>
		timeNow - instance.registeredAt < milliseconds;
}

export function getTabInstancesCookie(): TabInstancesCookie {
	const cookie = get(TAB_INSTANCES_COOKIE_NAME);
	if (cookie) {
		const data = JSON.parse(cookie) as TabInstancesCookie;
		const removeTabInstancesOlderThanADay = removeTabInstancesOlderThan(
			86400000, // 1 day
			Date.now()
		);

		return {
			...data,
			tabInstances: data.tabInstances.filter(removeTabInstancesOlderThanADay),
		} as TabInstancesCookie;
	}
	return {} as TabInstancesCookie;
}

export function setTabInstancesCookie(data: TabInstancesCookie): void {
	if (rootDomain === 'localhost') {
		// The cookie is undefined if a domain option is set on localhost
		set(TAB_INSTANCES_COOKIE_NAME, JSON.stringify(data));
	} else {
		set(TAB_INSTANCES_COOKIE_NAME, JSON.stringify(data), {
			domain: rootDomain,
		});
	}
}

// TODO: Unit test
export function isChatHistoryAuditAccessKeyValid(
	accessKey: ChatHistoryAuditAccessKey
): boolean {
	return accessKey.expiryTimestamp > Date.now();
}

// TODO: Unit test
export function getChatHistoryAuditAccessKey():
	| undefined
	| ChatHistoryAuditAccessKey {
	const cookie = get(CHAT_HISTORY_AUDIT_ACCESS_KEY_COOKIE_NAME);

	const accessKeys = cookie
		? (JSON.parse(cookie) as ChatHistoryAuditAccessKeyCookie)
		: ({} as ChatHistoryAuditAccessKeyCookie);

	const accessKey =
		accessKeys[
			CHAT_HISTORY_ACCESS_KEY_ENVIRONMENT as keyof ChatHistoryAuditAccessKeyCookie
		];

	return accessKey && isChatHistoryAuditAccessKeyValid(accessKey)
		? accessKey
		: undefined;
}
